import React, { useCallback, useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Loader2, Sparkles, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const STATUS_ITEMS = [
  ["stuck_jobs", "Trabajos atascados (>5 min sin señal)"],
  ["stuck_videos", "Vídeos atascados"],
  ["stuck_photos", "Fotos bloqueadas sin trabajo activo"],
  ["pending_reservations", "Créditos reservados pendientes"],
  ["failed_jobs", "Procesos fallidos sin archivar"],
  ["stuck_exports", "Descargas ZIP caducadas"],
  ["active_jobs", "Trabajos en curso ahora"],
];
const RESULT_ITEMS = [
  ["photo_jobs", "ediciones cerradas"], ["batch_jobs", "lotes cerrados"], ["videos", "vídeos cerrados"],
  ["photos_freed", "fotos liberadas"], ["reservations", "reservas resueltas"], ["credits_refunded", "créditos devueltos"],
  ["failed_archived", "fallidos archivados"], ["exports", "descargas cerradas"],
];

const Stat = ({ k, label, value }) => (
  <div className="rounded-2xl border border-white/5 bg-[#15131C] p-4" data-testid={`system-stat-${k}`}>
    <p className={`text-2xl font-bold tabular-nums ${value > 0 && k !== "active_jobs" ? "text-amber-300" : "text-white"}`}>{value ?? "–"}</p>
    <p className="text-xs text-slate-400 mt-0.5">{label}</p>
  </div>
);

export default function AdminSystem() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const load = useCallback(() => api.get("/admin/system/status").then((r) => setStatus(r.data)).catch((e) => toast.error(apiError(e))), []);
  useEffect(() => { load(); }, [load]);

  const clean = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/system/cleanup", null, { timeout: 120000 });
      setResult(data);
      toast.success("Sistema limpiado");
      await load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-8" data-testid="admin-system">
      <section className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="font-display text-xl font-semibold text-white">Estado del sistema</h2>
          <p className="text-sm text-slate-400 mt-1 max-w-xl">Cierra los procesos que llevan más de 5 minutos sin responder, libera las fotos bloqueadas y devuelve los créditos pendientes. Lo que está avanzando no se toca.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} disabled={busy} data-testid="system-refresh-btn" className="rounded-full border-white/15 bg-white/5 hover:bg-white/10 text-white">
            <RefreshCw className="w-4 h-4 mr-1.5" /> Actualizar
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button disabled={busy} data-testid="system-cleanup-btn" className="rounded-full bg-gradient-to-r from-violet-600 to-cyan-500 hover:brightness-110 text-white font-semibold transition-[filter]">
                {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Sparkles className="w-4 h-4 mr-1.5" />} Limpiar sistema
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="bg-[#1E1A29] border-white/10 text-white">
              <AlertDialogHeader>
                <AlertDialogTitle>¿Limpiar el sistema?</AlertDialogTitle>
                <AlertDialogDescription className="text-slate-400">Se cerrarán los procesos atascados y fallidos y se devolverán los créditos de las ediciones que no se completaron. Las ediciones guardadas se conservan.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="system-cleanup-cancel" className="bg-transparent border-white/15 text-white hover:bg-white/10">Cancelar</AlertDialogCancel>
                <AlertDialogAction data-testid="system-cleanup-confirm" onClick={clean} className="bg-gradient-to-r from-violet-600 to-cyan-500 text-white">Limpiar ahora</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </section>

      {!status ? <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-cyan-400" /></div> : (
        <section className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {STATUS_ITEMS.map(([k, label]) => <Stat key={k} k={k} label={label} value={status[k]} />)}
        </section>
      )}

      {result && (
        <section className="rounded-2xl border border-emerald-500/20 bg-emerald-500/5 p-5" data-testid="system-cleanup-result">
          <h3 className="text-sm font-semibold text-emerald-300 mb-3">Resultado de la última limpieza</h3>
          <div className="flex flex-wrap gap-2">
            {RESULT_ITEMS.map(([k, label]) => (
              <span key={k} data-testid={`system-result-${k}`} className="text-xs px-3 py-1 rounded-full bg-white/5 border border-white/10 text-slate-200">
                <b className="text-white tabular-nums">{result[k]}</b> {label}
              </span>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
